import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PRESENTATION_ELIGIBLE_ROLES } from '../store/registrationsSlice'
import type { Registration } from '../store/registrationsSlice'
import type { EventBranding } from '../store/eventsSlice'
import { Button } from './Button'

// ── Icons ──────────────────────────────────────────────────────────────────────

const LockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
)

const ClockIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
)

const XCircleIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" />
  </svg>
)

// ── Status indicator ──────────────────────────────────────────────────────────

type StatusVariant = 'pending' | 'accepted' | 'rejected'

const STATUS_COLOR: Record<StatusVariant, string> = {
  pending: '#B45309', // amber
  accepted: '#15803d', // green
  rejected: '#E61919', // red
}

/**
 * Presentation status as an icon + coloured label — deliberately not a pill/badge so it
 * doesn't visually compete with the role badge above it.
 */
function StatusIndicator({ variant, bodyFamily }: { variant: StatusVariant; bodyFamily: string }) {
  const { t } = useTranslation()
  const color = STATUS_COLOR[variant]
  const label =
    variant === 'pending'
      ? t('ticket_pres_status_pending')
      : variant === 'accepted'
        ? t('ticket_pres_status_accepted')
        : t('ticket_pres_status_rejected')
  const Icon =
    variant === 'pending' ? ClockIcon : variant === 'accepted' ? CheckCircleIcon : XCircleIcon

  return (
    <div
      className="flex items-center justify-center gap-1.5 text-sm font-bold tracking-wide uppercase"
      style={{ fontFamily: bodyFamily, color }}
    >
      <Icon />
      {label}
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  registration: Registration
  b: EventBranding
  headingFamily: string
  bodyFamily: string
  /** Opens the `TicketPresentationModal` for upload / re-upload (pre-submit statuses only). */
  onManageClick: () => void
  /** Downloads the submitted file. Shown when status is uploaded or accepted. */
  onDownload?: () => Promise<void>
  /** When false, hide the download button even if a file name is present. */
  canDownload?: boolean
}

/**
 * Participant-facing presentation status readout rendered inside the TicketPage
 * accepted-registration card.
 *
 * Before any submission (`reminder_sent`): "Manage presentation" opens the upload modal.
 * After submission (`uploaded` / `accepted`): file name + "Download my file" only — no manage.
 * Rejected: shows the rejected file's name + download (the file that was rejected) *and* a
 * "Manage presentation" button to re-upload.
 */
export function TicketPresentationSection({
  registration,
  b,
  headingFamily,
  bodyFamily,
  onManageClick,
  onDownload,
  canDownload = true,
}: Props) {
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const status = registration.presentation?.status ?? 'none'
  const fileName = registration.presentation?.fileName
  const isEligible = PRESENTATION_ELIGIBLE_ROLES.includes(registration.role ?? 'Audience')

  // Only show for eligible roles that have an active presentation lifecycle
  if (!isEligible || status === 'none') return null

  const isSubmitted = status === 'uploaded' || status === 'accepted'
  const canManage = status === 'reminder_sent' || status === 'rejected'
  // Rejected still has a file on record (the one that was turned down) — keep it downloadable.
  const hasDownloadableFile = isSubmitted || status === 'rejected'
  const showDownload = hasDownloadableFile && canDownload && !!onDownload

  const dividerStyle = { background: b.primary, opacity: 0.12 }

  async function handleDownload() {
    if (!onDownload || downloading) return
    setDownloading(true)
    setDownloadError(null)
    try {
      await onDownload()
    } catch {
      setDownloadError(t('pres_err_download'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Divider before section */}
      <div className="h-px w-full" style={dividerStyle} />

      {/* Section label */}
      <p
        className="text-center text-[10px] font-bold tracking-[0.22em] uppercase"
        style={{ fontFamily: bodyFamily, color: b.text, opacity: 0.4 }}
      >
        {t('ticket_pres_section_label')}
      </p>

      {/* Status — icon + coloured label, not a pill, so it doesn't compete with the role badge */}
      {status === 'uploaded' && <StatusIndicator variant="pending" bodyFamily={bodyFamily} />}
      {status === 'accepted' && <StatusIndicator variant="accepted" bodyFamily={bodyFamily} />}
      {status === 'rejected' && (
        <div className="flex flex-col items-center gap-2">
          <StatusIndicator variant="rejected" bodyFamily={bodyFamily} />
          {registration.presentation?.rejectionMessage && (
            <div
              className="w-full rounded-xl px-4 py-3 text-left text-sm leading-relaxed"
              style={{
                background: `${b.primary}10`,
                border: `1px solid ${b.primary}18`,
                color: b.text,
                opacity: 0.75,
              }}
            >
              <p
                className="mb-1 text-xs font-semibold tracking-wide uppercase"
                style={{ color: b.text, opacity: 0.5 }}
              >
                {t('ticket_pres_rejected_label')}
              </p>
              {registration.presentation.rejectionMessage}
            </div>
          )}
        </div>
      )}

      {/* Locked note — only while awaiting review or already accepted; rejected can re-upload */}
      {isSubmitted && (
        <p
          className="flex items-center justify-center gap-1.5 text-center text-xs leading-relaxed"
          style={{ color: b.text, opacity: 0.45, fontFamily: bodyFamily }}
        >
          <LockIcon />
          {t('ticket_pres_locked_note')}
        </p>
      )}

      {/* File name (submitted or rejected) */}
      {hasDownloadableFile && fileName && (
        <p
          className="w-full rounded-xl px-4 py-3 text-center text-sm font-medium break-all"
          style={{
            background: `${b.primary}10`,
            border: `1px solid ${b.primary}20`,
            color: b.text,
            fontFamily: bodyFamily,
          }}
        >
          {fileName}
        </p>
      )}

      {/* Re-upload / manage action comes before download so it stands out as the primary action */}
      {canManage && (
        <div className="flex w-full flex-col gap-1.5">
          <Button
            variant="unstyled"
            onClick={onManageClick}
            className="w-full rounded-xl px-5 py-3 text-sm font-bold tracking-wide transition-opacity hover:opacity-80"
            style={{
              background: b.accent,
              color: b.background,
              fontFamily: headingFamily,
            }}
          >
            {t('ticket_pres_manage_btn')}
          </Button>
          {/* Surface the size/type limit here too, not only inside the upload modal — see #225:
              a participant deciding whether a file is even worth attempting shouldn't have to
              open the modal first to find out. */}
          <p
            className="text-center text-xs"
            style={{ color: b.text, opacity: 0.45, fontFamily: bodyFamily }}
          >
            {t('pres_upload_constraints')}
          </p>
        </div>
      )}

      {/* Download the submitted (or previously rejected) file */}
      {hasDownloadableFile && (
        <div className="flex w-full flex-col gap-3">
          {showDownload && (
            <Button
              variant="unstyled"
              onClick={() => void handleDownload()}
              disabled={downloading}
              className="w-full rounded-xl border-2 px-5 py-3 text-sm font-bold tracking-wide transition-opacity hover:opacity-70 disabled:opacity-50"
              style={{
                background: 'transparent',
                borderColor: b.accent,
                color: b.accent,
                fontFamily: headingFamily,
              }}
            >
              {t('ticket_pres_download')}
            </Button>
          )}
          {downloadError && (
            <p role="alert" className="text-center text-xs text-red-600">
              {downloadError}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
